'use client';

import * as React from 'react';
import Link from 'next/link';
import {
  Building2Icon,
  ChevronRightIcon,
  FileTextIcon,
  SparklesIcon,
  TargetIcon,
  UserPlusIcon,
} from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import { Separator } from '@workspace/ui/components/separator';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';

function HeroPill(): React.JSX.Element {
  return (
    <motion.div
      initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
      animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="flex items-center justify-center"
    >
      <Link href="/features/mcp-api">
        <Badge
          variant="outline"
          className="group h-8 rounded-full px-3 text-xs font-medium shadow-xs duration-200 hover:bg-accent/50 sm:text-sm"
        >
          <div className="w-fit py-0.5 text-center text-xs text-cyan-500 sm:text-sm">
            New
          </div>
          <Separator orientation="vertical" className="mx-2" />
          Software 3.0 · MCP Ready · Claude Native
          <ChevronRightIcon className="ml-1.5 size-3 shrink-0 text-foreground transition-transform group-hover:translate-x-0.5" />
        </Badge>
      </Link>
    </motion.div>
  );
}

function HeroTitle(): React.JSX.Element {
  return (
    <motion.div
      initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
      animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
    >
      <h1 className="mt-6 text-center text-[48px] font-bold leading-[54px] tracking-[-1.2px] [font-kerning:none] sm:text-[56px] md:text-[64px] lg:text-[76px] lg:leading-[80px] lg:tracking-[-2px]">
        UK Company Intelligence,
        <br />
        Queryable by Any AI Agent.
      </h1>
    </motion.div>
  );
}

function HeroDescription(): React.JSX.Element {
  return (
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.4 }}
      className="mx-auto mt-3 max-w-[620px] text-balance text-center text-lg leading-[26px] text-muted-foreground sm:text-xl lg:mt-6"
    >
      5M+ live Companies House records, enriched and turned into sourced,
      checkable dossiers — served over MCP and REST. Plug the UK data layer
      into the stack you already run.
    </motion.p>
  );
}

function HeroButtons(): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      className="mx-auto flex w-full flex-col gap-2 px-7 sm:w-auto sm:flex-row sm:px-0"
    >
      <Link
        href={routes.dashboard.auth.SignUp}
        className={cn(
          buttonVariants({
            variant: 'default',
          }),
          'h-10 rounded-xl sm:h-9'
        )}
      >
        Start for Free
      </Link>
      <Link
        href="/docs"
        className={cn(
          buttonVariants({
            variant: 'outline',
          }),
          'h-10 rounded-xl sm:h-9'
        )}
      >
        Read the docs
      </Link>
    </motion.div>
  );
}

function MainDashedGridLines(): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.6, duration: 0.4 }}
    >
      <svg className="absolute left-[16.85%] top-0 hidden h-full w-px mask-[linear-gradient(to_bottom,#0000,#000_128px,#000_calc(100%-24px),#0000)] lg:block">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="var(--border)"
        />
      </svg>
      <svg className="absolute right-[16.85%] top-0 hidden h-full w-px mask-[linear-gradient(to_bottom,#0000,#000_128px,#000_calc(100%-24px),#0000)] lg:block">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="var(--border)"
        />
      </svg>
      <svg className="absolute bottom-[52px] left-[calc(50%-50vw)] hidden h-px w-screen mask-[linear-gradient(to_right,#0000,#000_100px,#000_calc(100%-100px),#0000)] lg:block">
        <line
          x1="0"
          y1="0.5"
          x2="100%"
          y2="0.5"
          strokeLinecap="round"
          strokeDasharray="5 5"
          stroke="var(--border)"
        />
      </svg>
    </motion.div>
  );
}

function useCountUp(target: number, duration = 1800, delay = 800) {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      const start = Date.now();
      const tick = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        setValue(Math.floor(target * ease));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, duration, delay]);
  return value;
}

function StatRow({
  icon,
  label,
  value,
  unit,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  unit?: string;
  accent: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-dashed border-neutral-200 dark:border-neutral-800 last:border-0">
      <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
        <span className={accent}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className={cn('font-mono text-sm font-semibold tabular-nums', accent)}>
        {value.toLocaleString()}
        {unit && <span className="ml-0.5 text-xs font-normal text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
}

function HeroStatTicker(): React.JSX.Element {
  const prospectsFound = useCountUp(147, 1800, 900);
  const profilesEnriched = useCountUp(132, 1800, 1000);
  const battlecardsReady = useCountUp(38, 1800, 1100);
  const companiesIndexed = useCountUp(5128403, 2000, 1400);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.5 }}
      className="mx-auto mt-6 w-full max-w-sm lg:mt-10"
    >
      <div className="overflow-hidden rounded-xl border bg-background shadow-lg dark:border-neutral-800">
        {/* Terminal header */}
        <div className="flex items-center gap-2 border-b bg-neutral-50 px-4 py-2.5 dark:bg-neutral-900 dark:border-neutral-800">
          <div className="size-2.5 rounded-full bg-red-400" />
          <div className="size-2.5 rounded-full bg-yellow-400" />
          <div className="size-2.5 rounded-full bg-green-400" />
          <span className="ml-2 font-mono text-xs text-muted-foreground">
            kithflow — live activity
          </span>
          <span className="ml-auto flex items-center gap-1.5 text-xs text-green-500">
            <span className="inline-block size-1.5 rounded-full bg-green-500 animate-pulse" />
            running
          </span>
        </div>
        {/* Stats */}
        <div className="px-5 py-1">
          <StatRow
            icon={<TargetIcon className="size-3.5" />}
            label="Prospects found today"
            value={prospectsFound}
            accent="text-cyan-500"
          />
          <StatRow
            icon={<UserPlusIcon className="size-3.5" />}
            label="Profiles enriched today"
            value={profilesEnriched}
            accent="text-blue-500"
          />
          <StatRow
            icon={<FileTextIcon className="size-3.5" />}
            label="Battlecards ready"
            value={battlecardsReady}
            accent="text-violet-500"
          />
          <StatRow
            icon={<Building2Icon className="size-3.5" />}
            label="UK companies indexed"
            value={companiesIndexed}
            accent="text-amber-500"
          />
        </div>
        {/* Footer hint */}
        <div className="flex items-center gap-1.5 border-t bg-neutral-50 px-4 py-2 dark:bg-neutral-900 dark:border-neutral-800">
          <SparklesIcon className="size-3 text-cyan-500" />
          <span className="font-mono text-[11px] text-muted-foreground">
            Intent Engine last run: 2 min ago
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export function Hero(): React.JSX.Element {
  return (
    <GridSection className="overflow-x-hidden">
      <MainDashedGridLines />
      <div className="mx-auto mt-16 flex flex-col gap-6 px-2 sm:mt-20 sm:px-1 md:mt-24 lg:mt-32">
        <div className="gap-2">
          <HeroPill />
          <HeroTitle />
        </div>
        <HeroDescription />
        <HeroButtons />
        <HeroStatTicker />
      </div>
    </GridSection>
  );
}
